import { Link, useNavigate } from 'react-router-dom';
import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
  useMsal,
} from '@azure/msal-react';
import { FC, useState } from 'react';
import { SocialLinks } from '@shared-components';
import { useProperties, useWishlist } from '@providers';
import { useLocationChange } from '@hooks';
import { languages } from '@i18n';
import {
  countryCodeFromCulture,
  extractFilter,
  loginRequest,
  makeUnicodeFlagEmoji,
  xssSanitize,
} from '@utils';
import { MobileSubMenu } from './MobileSubMenu.tsx';
import './MobileMenu.scss';
import { SiteLogoLink } from '../SiteLogo';
import { useTranslation } from 'react-i18next';

type MobileMenuProps = {
  handleClose: () => void;
  isOpen: boolean;
};

export const MobileMenu: FC<MobileMenuProps> = (props) => {
  const msalContext = useMsal();
  const { properties, culture, setCulture } = useProperties();
  const { t } = useTranslation();
  const { wishlist } = useWishlist();
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState('');

  const [isPropertiesMenuOpen, setIsPropertiesMenuOpen] = useState(false);
  const [isLanguagesMenuOpen, setIsLanguagesMenuOpen] = useState(false);
  const [isAdminMenuOpen, setIsAdminMenuOpen] = useState(false);

  const aims = extractFilter('aim', properties).sort(
    (a, b) => b.count - a.count,
  );
  const country = countryCodeFromCulture(culture);

  useLocationChange(props.handleClose);

  const handleLogin = () => {
    msalContext.instance.loginRedirect(loginRequest).catch((error) => {
      console.log(error);
    });
  };

  const handleLogout = () => {
    msalContext.instance
      .logoutRedirect({
        postLogoutRedirectUri: '/',
      })
      .catch((error) => {
        console.log(error);
      });
  };

  return (
    <div
      id='ltn__utilize-mobile-menu'
      className={`d-xl-none ltn__utilize ltn__utilize-mobile-menu ${props.isOpen ? 'ltn__utilize-open' : ''}`}
    >
      <div className='ltn__utilize-menu-inner ltn__scrollbar'>
        <div className='ltn__utilize-menu-head'>
          <SiteLogoLink />
          <button className='ltn__utilize-close' onClick={props.handleClose}>
            ×
          </button>
        </div>
        <div className='ltn__utilize-menu-search-form'>
          <input
            type='text'
            name='search'
            placeholder={t('searchForm.searchProperties') + '...'}
            onChange={(e) => setSearchText(e.target.value)}
            value={searchText}
          />
          <button
            onClick={() => {
              navigate(`/properties?search=${xssSanitize(searchText)}`);
              props.handleClose();
            }}
          >
            <i className='fas fa-search'></i>
          </button>
        </div>
        <div className='ltn__utilize-menu'>
          <ul>
            <li>
              <Link to='/'>{t('header.home')}</Link>
            </li>
            <MobileSubMenu
              isExpanded={isPropertiesMenuOpen}
              onExpand={setIsPropertiesMenuOpen}
              title={t('header.properties')}
            >
              <li>
                <Link to='/properties'>{t('header.allProperties')}</Link>
              </li>
              {aims.map(({ text }) => (
                <li key={text}>
                  <Link to={`/properties?aim=${text}`}>{text}</Link>
                </li>
              ))}
              <li>
                <Link to='/wishlist'>{t('header.wishlist')}</Link>
              </li>
            </MobileSubMenu>
            <li>
              <Link to='/add-listing'>{t('header.addListing')}</Link>
            </li>
            <li>
              <Link to='/request-property'>{t('header.request')}</Link>
            </li>
            <li>
              <Link to='/about'>{t('header.about')}</Link>
            </li>
            <li>
              <Link to='/contact'>{t('header.contact')}</Link>
            </li>
            <AuthenticatedTemplate>
              <MobileSubMenu
                title='Admin'
                isExpanded={isAdminMenuOpen}
                onExpand={setIsAdminMenuOpen}
              >
                {/*<li>*/}
                {/*  <ToggleButton*/}
                {/*    id='admin-mode-toggle'*/}
                {/*    value='0'*/}
                {/*    type='checkbox'*/}
                {/*    variant='outline-secondary'*/}
                {/*    size='sm'*/}
                {/*    checked={false}*/}
                {/*  >*/}
                {/*    {t('header.adminControls')}*/}
                {/*  </ToggleButton>*/}
                {/*</li>*/}
                <li>
                  <Link to='/admin/messages'>{t('header.adminMessages')}</Link>
                </li>
                <li>
                  <button onClick={handleLogout}>{t('header.logout')}</button>
                </li>
              </MobileSubMenu>
            </AuthenticatedTemplate>
          </ul>
        </div>
        <div className='ltn__utilize-buttons ltn__utilize-buttons-2'>
          <ul>
            <li>
              <Link to='/wishlist' title={t('header.wishlist')}>
                <span className='utilize-btn-icon'>
                  <i className='fas fa-heart'></i>
                  {wishlist.length ? <sup>{wishlist.length}</sup> : ''}
                </span>
                {t('header.wishlist')}
              </Link>
            </li>
            <MobileSubMenu
              icon={
                <span className='utilize-btn-icon'>
                  {makeUnicodeFlagEmoji(country)}
                </span>
              }
              isExpanded={isLanguagesMenuOpen}
              onExpand={setIsLanguagesMenuOpen}
              title={t('header.languages.languageSelect')}
            >
              {languages.map(({ code, label }) => (
                <li key={code}>
                  <a
                    className='language-btn text-uppercase d-flex align-items-center'
                    onClick={() => setCulture(code)}
                  >
                    <span className='round-icon'>
                      {makeUnicodeFlagEmoji(countryCodeFromCulture(code))}
                    </span>
                    {label}
                  </a>
                </li>
              ))}
            </MobileSubMenu>
          </ul>
        </div>
        <SocialLinks className='ltn__social-media-2'>
          <UnauthenticatedTemplate>
            <li>
              <a onClick={handleLogin} title='Employee login'>
                <i className='flaticon-user'></i>
              </a>
            </li>
          </UnauthenticatedTemplate>
        </SocialLinks>
      </div>
    </div>
  );
};
